import { AxiosError } from "axios";
import { useMutation, useQuery } from "react-query";
import HttpService from "../../services/http";
import { queryClient } from "../../providers/QueryProvider";

const base = (customer: string, entity: string) =>
  `/${customer}/${entity}/providers/whatsapp/library`;

export const useGetWhatsappLibrary = (
  customer_identification: string,
  entity_identification: string,
  enabled = true
) =>
  useQuery(
    ["providers-whatsapp-library", entity_identification],
    async () => {
      const response = await HttpService.get(
        base(customer_identification, entity_identification)
      );
      return response?.data?.element;
    },
    {
      enabled: enabled && !!customer_identification && !!entity_identification,
      retry: false,
    }
  );

type DeleteTemplateProps = {
  customer_identification: string;
  entity_identification: string;
  key: string;
  language?: string | null;
};

export const useDeleteWhatsappLibraryTemplate = () =>
  useMutation<any, AxiosError<any, any>, DeleteTemplateProps>(
    ({
      customer_identification,
      entity_identification,
      key,
      language,
    }: DeleteTemplateProps) =>
      HttpService.delete(
        `${base(customer_identification, entity_identification)}/${key}`,
        { params: language ? { language } : undefined }
      ),
    {
      onSuccess: (_value, variables) => {
        queryClient.invalidateQueries([
          "providers-whatsapp-library",
          variables.entity_identification,
        ]);
      },
    }
  );

type UpsertTemplateProps = {
  customer_identification: string;
  entity_identification: string;
  key: string;
  definition: Record<string, any>;
};

export const useUpsertWhatsappLibraryTemplate = () =>
  useMutation<any, AxiosError<any, any>, UpsertTemplateProps>(
    ({
      customer_identification,
      entity_identification,
      key,
      definition,
    }: UpsertTemplateProps) =>
      HttpService.put(
        `${base(
          customer_identification,
          entity_identification
        )}/catalog/${key}`,
        definition
      ),
    {
      onSuccess: (_value, variables) => {
        queryClient.invalidateQueries([
          "providers-whatsapp-library",
          variables.entity_identification,
        ]);
      },
    }
  );

const root = (customer: string, entity: string) =>
  `/${customer}/${entity}/providers`;

const useMonitoringQuery = (
  key: any[],
  customer: string,
  entity: string,
  path: string,
  params?: Record<string, any>,
  enabled = true
) =>
  useQuery(
    key,
    async () => {
      const response = await HttpService.get(
        `${root(customer, entity)}/${path}`,
        { params }
      );
      return response?.data?.element;
    },
    { enabled: enabled && !!customer && !!entity, retry: false }
  );

export const useTwilioAccount = (c: string, e: string, enabled = true) =>
  useMonitoringQuery(
    ["providers-twilio-account", e],
    c,
    e,
    "twilio/account",
    undefined,
    enabled
  );
export const useTwilioSenders = (c: string, e: string, enabled = true) =>
  useMonitoringQuery(
    ["providers-twilio-senders", e],
    c,
    e,
    "twilio/senders",
    undefined,
    enabled
  );
export const useTwilioNumbers = (c: string, e: string, enabled = true) =>
  useMonitoringQuery(
    ["providers-twilio-numbers", e],
    c,
    e,
    "twilio/numbers",
    undefined,
    enabled
  );
export const useTwilioUsage = (
  c: string,
  e: string,
  period: string,
  enabled = true
) =>
  useMonitoringQuery(
    ["providers-twilio-usage", e, period],
    c,
    e,
    "twilio/usage",
    { period },
    enabled
  );
export const useTwilioMessages = (
  c: string,
  e: string,
  params: Record<string, any>,
  enabled = true
) =>
  useMonitoringQuery(
    ["providers-twilio-messages", e, params],
    c,
    e,
    "twilio/messages",
    params,
    enabled
  );

export const useBrevoAccount = (c: string, e: string, enabled = true) =>
  useMonitoringQuery(
    ["providers-brevo-account", e],
    c,
    e,
    "brevo/account",
    undefined,
    enabled
  );
export const useBrevoStats = (
  c: string,
  e: string,
  period: number,
  enabled = true
) =>
  useMonitoringQuery(
    ["providers-brevo-stats", e, period],
    c,
    e,
    "brevo/stats",
    { period },
    enabled
  );
export const useBrevoDomains = (c: string, e: string, enabled = true) =>
  useMonitoringQuery(
    ["providers-brevo-domains", e],
    c,
    e,
    "brevo/domains",
    undefined,
    enabled
  );

export const useKmmQueueSummary = (c: string, e: string, enabled = true) =>
  useMonitoringQuery(
    ["providers-kmm-summary", e],
    c,
    e,
    "kmm/queue/summary",
    undefined,
    enabled
  );
export const useKmmQueueMessages = (
  c: string,
  e: string,
  params: Record<string, any>,
  enabled = true
) =>
  useMonitoringQuery(
    ["providers-kmm-messages", e, params],
    c,
    e,
    "kmm/queue/messages",
    params,
    enabled
  );

type QueueActionProps = {
  customer_identification: string;
  entity_identification: string;
  message_id: string;
};

const invalidateQueue = (entity: string) => {
  queryClient.invalidateQueries(["providers-kmm-summary", entity]);
  queryClient.invalidateQueries(["providers-kmm-messages", entity]);
};

export const useDeleteQueueMessage = () =>
  useMutation<any, AxiosError<any, any>, QueueActionProps>(
    ({
      customer_identification,
      entity_identification,
      message_id,
    }: QueueActionProps) =>
      HttpService.delete(
        `${root(
          customer_identification,
          entity_identification
        )}/kmm/queue/messages/${message_id}`
      ),
    { onSuccess: (_v, vars) => invalidateQueue(vars.entity_identification) }
  );

export const useRetryQueueMessage = () =>
  useMutation<any, AxiosError<any, any>, QueueActionProps>(
    ({
      customer_identification,
      entity_identification,
      message_id,
    }: QueueActionProps) =>
      HttpService.post(
        `${root(
          customer_identification,
          entity_identification
        )}/kmm/queue/messages/${message_id}/retry`,
        {}
      ),
    { onSuccess: (_v, vars) => invalidateQueue(vars.entity_identification) }
  );

export const useTwilioMessageDetail = (
  c: string,
  e: string,
  sid: string | null,
  enabled = true
) =>
  useMonitoringQuery(
    ["providers-twilio-message", e, sid],
    c,
    e,
    `twilio/messages/${sid}`,
    undefined,
    enabled && !!sid
  );
export const useKmmMessageDetail = (
  c: string,
  e: string,
  id: string | null,
  enabled = true
) =>
  useMonitoringQuery(
    ["providers-kmm-message", e, id],
    c,
    e,
    `kmm/queue/messages/${id}`,
    undefined,
    enabled && !!id
  );
